import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import React, { useEffect, useState } from 'react'
import className from 'classnames/bind'

import style from './MyhouseInsuranceSelectBox.module.scss'

import ArrowDown13x8 from '@Ui/images/myhouse/ArrowDown13x8.svg'
import useInsurance from '@Hooks/myhouse/insurance/useInsurance'
import useInsuranceRegister from '@Hooks/myhouse/insurance/useInsuranceRegister'
import InsuranceProduct from '@Types/entities/InsuranceProduct'
import InsuranceCompany from '@Types/entities/InsuranceCompany'
import { FETCH_STATUS } from '@Root/contants'

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

const cx = className.bind(style)

type Props = {
  insuranceCompanies: InsuranceCompany[]
  insuranceProducts: InsuranceProduct[]
}

interface YearMonthOption {
  key: string
  value: string
  text: string
}

const MyhouseInsuranceSelectBox: React.FC<Props> = ({ insuranceCompanies, insuranceProducts }: Props) => {
  const { insurance, insuranceInitialState } = useInsurance()
  const { insuranceRegister, setCompanyCode, setProductSequence, setStartYearMonth, setSubscriptionPeriod } = useInsuranceRegister()

  const emptyProducts = [] as InsuranceProduct[]
  const [companyProducts, setCompanyProducts] = useState(emptyProducts)

  useEffect(() => {
    return () => {
      insuranceInitialState()
    }
  }, [])

  useEffect(() => {
    const productList = [] as InsuranceProduct[]
    Object.keys(insuranceProducts).map((product, idx) => {
      if (insuranceProducts[idx].companyCode === insuranceRegister.companyCode) {
        productList.push(insuranceProducts[idx])
      }
    })
    setCompanyProducts(productList)
  }, [insuranceRegister.companyCode])

  const startYearMonths = () => {
    const yearMonthList = [] as YearMonthOption[]

    let endDate = dayjs().startOf('month')
    let startDate = dayjs().startOf('month').subtract(4, 'year')
    if (insurance.subscribedInsurance.fetchStatus === FETCH_STATUS.SUCCESS) {
      const subscribedStartYearMonth = dayjs(insurance.subscribedInsurance.startYM)
      startDate = startDate.isAfter(subscribedStartYearMonth) ? subscribedStartYearMonth : startDate
    }
    while (endDate.isSameOrAfter(startDate, 'month')) {
      yearMonthList.push({
        key: endDate.format('YYYYMM'),
        value: endDate.format('YYYYMM'),
        text: endDate.format('YYYY.MM.'),
      } as YearMonthOption)
      endDate = endDate.subtract(1, 'month')
    }
    return yearMonthList
  }

  const handleCompanyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const companyCode = event.target.value
    setCompanyCode(companyCode)
    setProductSequence(0)
  }
  const handleProductChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const productSequence = event.target.value
    setProductSequence(Number(productSequence))
  }
  const handleYearMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const startYearMonth = event.target.value
    setStartYearMonth(startYearMonth)
  }
  const handleSubscriptionPeriod = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const subscriptionPeriod = event.target.value
    setSubscriptionPeriod(Number(subscriptionPeriod))
  }

  return (
    <>
      <div key="insuranceCompanies" className={cx('myhouse_list_item')}>
        <label className={cx('myhouse_item_label')} htmlFor={cx('myhouse_item_label')}>
          보험사
        </label>

        <div className={cx('myhouse_item_select')}>
          <select id="companies" onChange={(e) => handleCompanyChange(e)} value={insuranceRegister.companyCode}>
            <option key="companyOption" value="">
              보험사 선택
            </option>
            {Object.keys(insuranceCompanies).map((company, index) => (
              <option key={'company-' + index} value={insuranceCompanies[index].companyCode}>
                {insuranceCompanies[index].companyName}
              </option>
            ))}
          </select>

          <span className={cx('myhouse_item_icon', 'type_arrow')} role="img">
            <ArrowDown13x8 />
          </span>
        </div>
      </div>
      <div key="insuranceProducts" className={cx('myhouse_list_item')}>
        <label className={cx('myhouse_item_label')} htmlFor={cx('myhouse_item_label')}>
          보험 종류
        </label>

        <div className={cx('myhouse_item_select')}>
          <select id="products" onChange={(e) => handleProductChange(e)} value={insuranceRegister.productSequence}>
            <option key="productOption" value="0">
              보험 종류 선택
            </option>
            {companyProducts.map((product, index) => (
              <option key={'product-' + index} value={product.productSequence}>
                {product.companyCode + ' ' + product.productName}
              </option>
            ))}
          </select>

          <span className={cx('myhouse_item_icon', 'type_arrow')} role="img">
            <ArrowDown13x8 />
          </span>
        </div>
      </div>
      <div key="startYearMonth" className={cx('myhouse_list_item')}>
        <label className={cx('myhouse_item_label')} htmlFor={cx('myhouse_item_label')}>
          보험 시작 기간
        </label>

        <div className={cx('myhouse_item_select')}>
          <select id="yearMonth" onChange={(e) => handleYearMonthChange(e)} value={insuranceRegister.startYearMonth}>
            <option key="yearMonthOption" value="">
              보험 시작 연월 선택
            </option>
            {startYearMonths().map((yearMonth) => (
              <option key={yearMonth.key} value={yearMonth.value}>
                {yearMonth.text}
              </option>
            ))}
          </select>

          <span className={cx('myhouse_item_icon', 'type_arrow')} role="img">
            <ArrowDown13x8 />
          </span>
        </div>
      </div>
      <div key="subscriptionPeriod" className={cx('myhouse_list_item')}>
        <label className={cx('myhouse_item_label')} htmlFor={cx('myhouse_item_label')}>
          가입 기간
        </label>

        <div className={cx('myhouse_item_select')}>
          <select id="period" onChange={(e) => handleSubscriptionPeriod(e)} value={insuranceRegister.subscriptionPeriod}>
            <option key="period_6" value={6}>
              6개월
            </option>
            <option key="period_12" value={12}>
              12개월(1년)
            </option>
            <option key="period_24" value={24}>
              24개월(2년)
            </option>
            <option key="period_36" value={36}>
              36개월(3년)
            </option>
            <option key="period_48" value={48}>
              48개월(4년)
            </option>
          </select>

          <span className={cx('myhouse_item_icon', 'type_arrow')} role="img">
            <ArrowDown13x8 />
          </span>
        </div>
      </div>
    </>
  )
}

export default MyhouseInsuranceSelectBox
